import { Bot, Clock, User, X } from 'lucide-react';
import { ConversationMessage } from '@/types/agent/setup';

interface SetupTranscriptDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: ConversationMessage[] | null;
}

export const SetupTranscriptDrawer = ({ isOpen, onClose, transcript }: SetupTranscriptDrawerProps) => {
  const formatTimestamp = (timestamp: number) => {
    const totalSeconds = Math.floor(timestamp);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  const hasTranscript = transcript && transcript.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose}>
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ padding: "32px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Setup Conversation</h3>
            <p className="text-sm text-gray-600 mt-1">Review what was discussed in this setup conversation</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl font-bold" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>

        {!hasTranscript && (
          <div className="text-center py-12">
            <p className="text-gray-500">No conversation transcript available.</p>
          </div>
        )}

        {hasTranscript && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">Conversation transcript ({transcript.length} messages)</div>

            <div className="space-y-4">
              {transcript.map((message: any, index) => {
                const isAgent = message.speaker === "agent";
                return (
                  <div key={index} className={`flex gap-3 ${isAgent ? "justify-start" : "justify-end"}`}>
                    <div className={`flex gap-3 max-w-[80%] ${!isAgent ? "flex-row-reverse" : ""}`}>
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          isAgent ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {isAgent ? <Bot className="w-4 h-4" /> : <User className="w-4 h-4" />}
                      </div>

                      <div
                        className={`p-3 rounded-lg ${
                          isAgent
                            ? "bg-blue-50 text-blue-900 border border-blue-100"
                            : "bg-gray-50 text-gray-900 border border-gray-200"
                        }`}
                      >
                        <div className="text-sm leading-relaxed">{message.text}</div>
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          <span>{formatTimestamp(message.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-8">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
